// Panel Representativeness — how well the VMO2 panel reflects the UK population.
// Compares panel demographic composition (rolling 30d, end of month) against
// ONS 2021 Census (age/gender) and ONS mid-2023 estimates (geography).
import { useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useMonth } from '../context/MonthContext';
import { useCategory } from '../context/CategoryContext';
import { useAuth } from '../context/AuthContext';
import { Panel } from '../utils/api';
import { BarChart, LineChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { COLORS, AGE_ORDER, fmtNum, fmtMonth, fmtPct } from '../utils/theme';
import { geoName } from '../utils/postcodes';
import {
  UK_CENSUS_AGE_PCT,
  UK_CENSUS_GENDER_PCT,
  UK_CENSUS_GEO_PCT,
  CENSUS_SOURCE,
} from '../utils/census';

const GENDER_LABEL = { M: 'Male', F: 'Female' };
const UK_POP_TOTAL = 67_500_000;
// 0-17 leads the age axis: census-only band (panel is 18+, so Panel Share is empty).
const AGE_DISPLAY_ORDER = ['0-17', ...AGE_ORDER];

// Compute panel share % for a segment row
const pShare = (universe, total) =>
  total ? +((universe / total) * 100).toFixed(2) : 0;

export default function PanelRepresentativenessView() {
  const { month } = useMonth();
  const { category } = useCategory();
  const { allowedCategories } = useAuth();
  const { latestMonth, loading: metaLoading } = useMetaData();

  const selectedMonth = month || latestMonth;

  // panel_size / avg_daily_uniques are category-independent, but /panel/trend
  // still runs check_category_access on the category param. A category-restricted
  // user has no access to 'ALL', so request an allowed category instead (any works —
  // the chart ignores cat_users). Falls back to 'ALL' for unrestricted users.
  const trendCat = category || (allowedCategories && allowedCategories[0]) || 'ALL';

  const demo = useApi(
    () => Panel.demographics({ month_id: selectedMonth }),
    { selectedMonth },
    !!selectedMonth,
  );

  // Panel size trend (panel-wide metrics; category-agnostic)
  const trend = useApi(
    () => Panel.trend({ category: trendCat, session_type: 'open_app' }),
    { trendCat },
    true,
  );
  const trendMonths = useMemo(() => (trend.data || []).map(r => r.month_id), [trend.data]);
  const trendSeries = useMemo(() => trend.data ? [
    { name: 'Panel Size (rolling 30d)', color: COLORS.indigo, data: trend.data.map(r => r.panel_size ?? null) },
    { name: 'Avg Daily Actives',        color: COLORS.teal,   data: trend.data.map(r => r.avg_daily_uniques ?? null) },
  ] : [], [trend.data]);

  const rows        = demo.data || [];
  const overall     = rows.find(r => r.attribute === 'overall');
  const totalPanel  = overall?.universe ?? 0;
  const coverage    = totalPanel ? +((totalPanel / UK_POP_TOTAL) * 100).toFixed(1) : null;

  // ── Age ────────────────────────────────────────────────────────────────────
  // Census-driven order: 0-17 has a census value but no panel data (panel is 18+),
  // so its Panel Share bar is intentionally left empty (null → no bar).
  const ageData = useMemo(() => {
    const bands = rows.filter(r => r.attribute === 'age_band');
    return AGE_DISPLAY_ORDER
      .filter(b => UK_CENSUS_AGE_PCT[b] != null)
      .map(b => {
        const panelRow = bands.find(r => r.attribute_value === b);
        return {
          band:      b,
          panelPct:  panelRow ? pShare(panelRow.universe, totalPanel) : null,
          censusPct: UK_CENSUS_AGE_PCT[b],
        };
      });
  }, [rows, totalPanel]);

  const ageCategories  = ageData.map(r => r.band);
  const agePanelPcts   = ageData.map(r => r.panelPct);
  const ageCensusPcts  = ageData.map(r => r.censusPct);

  // ── Gender ─────────────────────────────────────────────────────────────────
  const genderRows = useMemo(
    () => rows.filter(r => r.attribute === 'gender' && UK_CENSUS_GENDER_PCT[r.attribute_value] != null),
    [rows],
  );

  const genderCategories = genderRows.map(r => GENDER_LABEL[r.attribute_value] || r.attribute_value);
  const genderPanelPcts  = genderRows.map(r => pShare(r.universe, totalPanel));
  const genderCensusPcts = genderRows.map(r => UK_CENSUS_GENDER_PCT[r.attribute_value]);

  // ── Geography — sorted desc by panel index (most over-represented first) ──
  const geoRows = useMemo(() => {
    if (!totalPanel) return [];
    return rows
      .filter(r => r.attribute === 'postcode_area' && UK_CENSUS_GEO_PCT[r.attribute_value] != null && r.attribute_value !== 'BT' && r.attribute_value !== 'Unknown')
      .map(r => {
        const panelPct  = pShare(r.universe, totalPanel);
        const censusPct = UK_CENSUS_GEO_PCT[r.attribute_value];
        const index     = censusPct ? (panelPct / censusPct) * 100 : 0;
        return { ...r, panelPct, censusPct, index };
      })
      .sort((a, b) => b.index - a.index);
  }, [rows, totalPanel]);

  // Top 10 over-represented (highest index first)
  const overRows = geoRows.slice(0, 10);
  // Top 10 under-represented (lowest index first — reverse end of list)
  const underRows = [...geoRows].slice(-10).reverse();

  const geoSeries = rows => [
    { name: 'Panel Share %', color: COLORS.indigo, data: rows.map(r => r.panelPct) },
    { name: 'Census %',      color: COLORS.teal,   data: rows.map(r => r.censusPct) },
  ];

  const pctFmt = v => `${v !== undefined ? v : ''}%`;

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      {/* ── KPI tiles ──────────────────────────────────────────────────── */}
      {demo.loading && <ChartLoader height={100} />}
      {demo.error   && <ChartError message={demo.error} height={100} />}

      {!demo.loading && !demo.error && overall && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card" style={{ background: COLORS.teal, borderColor: COLORS.teal }}>
            <div className="kpi-label" style={{ color: 'rgba(255,255,255,0.85)' }}>VMO2 Panel Size</div>
            <div className="kpi-value" style={{ color: '#fff' }}>{fmtNum(totalPanel)}</div>
            <div className="kpi-delta neutral" style={{ color: 'rgba(255,255,255,0.75)' }}>
              rolling 30-day uniques · {fmtMonth(selectedMonth)}
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">UK Population</div>
            <div className="kpi-value">67.5M</div>
            <div className="kpi-delta neutral">ONS mid-2023 estimate</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Panel Coverage</div>
            <div className="kpi-value">{coverage != null ? `${coverage}%` : '—'}</div>
            <div className="kpi-delta neutral">panel size ÷ UK population</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Data Month</div>
            <div className="kpi-value" style={{ fontSize: 20 }}>{fmtMonth(selectedMonth)}</div>
            <div className="kpi-delta neutral">end-of-month rolling 30d snapshot</div>
          </div>
        </div>
      )}

      {/* ── Panel Size Trend ───────────────────────────────────────────── */}
      <ChartCard
        title="Panel Size Trend"
        sub="Rolling 30-day panel size vs average daily actives — full history"
        style={{ marginBottom: 16 }}
      >
        {trend.loading && <ChartLoader height={300} />}
        {trend.error   && <ChartError message={trend.error} height={300} />}
        {!trend.loading && !trend.error && trendMonths.length > 0 && (
          <LineChart categories={trendMonths.map(fmtMonth)} series={trendSeries} height={300} />
        )}
      </ChartCard>

      {/* ── Charts ─────────────────────────────────────────────────────── */}
      {!demo.loading && !demo.error && rows.length > 0 && (
        <>
          {/* Age + Gender side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <ChartCard
              title="Age Band — Panel vs Census"
              sub={`Panel share % vs ONS 2021 Census (all ages; panel is 18+) · ${fmtMonth(selectedMonth)}`}
            >
              <BarChart
                categories={ageCategories}
                series={[
                  { name: 'Panel Share %', color: COLORS.indigo, data: agePanelPcts },
                  { name: 'Census %',      color: COLORS.teal,   data: ageCensusPcts },
                ]}
                height={300}
                xFormatter={pctFmt}
              />
            </ChartCard>

            <ChartCard
              title="Gender — Panel vs Census"
              sub={`Panel share % vs ONS 2021 Census · ${fmtMonth(selectedMonth)}`}
            >
              <BarChart
                categories={genderCategories}
                series={[
                  { name: 'Panel Share %', color: COLORS.indigo, data: genderPanelPcts },
                  { name: 'Census %',      color: COLORS.teal,   data: genderCensusPcts },
                ]}
                height={300}
                xFormatter={pctFmt}
              />
            </ChartCard>
          </div>

          {/* Geography — top 10 over / under side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <ChartCard
              title="Top 10 — Over-represented Areas"
              sub={`Panel share % exceeds census · ${fmtMonth(selectedMonth)}`}
            >
              <BarChart
                categories={overRows.map(r => geoName(r.attribute_value))}
                series={geoSeries(overRows)}
                horizontal
                height={360}
                xFormatter={pctFmt}
              />
            </ChartCard>

            <ChartCard
              title="Top 10 — Under-represented Areas"
              sub={`Panel share % below census · ${fmtMonth(selectedMonth)}`}
            >
              <BarChart
                categories={underRows.map(r => geoName(r.attribute_value))}
                series={geoSeries(underRows)}
                horizontal
                height={360}
                xFormatter={pctFmt}
              />
            </ChartCard>
          </div>

          {/* Geography — all areas */}
          <ChartCard
            title={`All Areas — Panel vs Census (${geoRows.length} areas, sorted by panel index desc)`}
            sub={`Panel share % vs ONS mid-2023 population estimates · ${fmtMonth(selectedMonth)}`}
            style={{ marginBottom: 12 }}
          >
            <BarChart
              categories={geoRows.map(r => geoName(r.attribute_value))}
              series={geoSeries(geoRows)}
              horizontal
              height={Math.max(400, geoRows.length * 30 + 80)}
              xFormatter={pctFmt}
            />
          </ChartCard>

          {/* Census citation + exclusion note */}
          <div style={{
            padding: '8px 12px',
            fontSize: 11,
            color: 'var(--text-muted)',
            borderTop: '1px solid var(--border)',
            lineHeight: 1.6,
          }}>
            <strong>Census reference:</strong> {CENSUS_SOURCE}
            {' '}Panel coverage = rolling 30-day unique customers ÷ UK population estimate.
            Geography sorted by panel index (panel share % ÷ census share % × 100) descending —
            areas above 100 are over-represented in the panel relative to their population.
            <br />
            * Geography charts exclude <strong>Belfast (BT)</strong> — no panel coverage in Northern Ireland —
            and <strong>Unknown</strong> postcode areas where census attribution is not available.
          </div>
        </>
      )}
    </div>
  );
}
